import type { MemberConsolidatedStatement } from "@/services/types";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";

function Money({ value }: { value: number }) {
  return <CurrencyDisplay amount={value} hideSymbol className="text-sm font-medium" />;
}

export function ConsolidatedStatementPrint({ statement }: { statement: MemberConsolidatedStatement }) {
  return (
    <div>
      <div className="mb-5 grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-foreground-800">
        <p><span className="text-foreground-500">Member:</span> <span className="font-semibold text-foreground-950">{statement.memberName}</span></p>
        <p><span className="text-foreground-500">Code:</span> <span className="tnum font-medium">{statement.memberCode}</span></p>
        <p><span className="text-foreground-500">Phone:</span> <span className="tnum font-medium">{statement.memberPhone}</span></p>
        <p><span className="text-foreground-500">City:</span> {statement.city}</p>
        <p className="col-span-2"><span className="text-foreground-500">Generated:</span> <span className="tnum font-medium">{formatDate(statement.generatedAt)}</span> — consolidated across all groups</p>
      </div>

      <h3 className="mb-2.5 border-b border-background-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-foreground-700">Position by group</h3>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-background-100 text-foreground-600">
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Group</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Ticket</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Total charged</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Dividend</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Paid</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Outstanding</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Won</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Cycles left</th>
          </tr>
        </thead>
        <tbody>
          {statement.groups.map((g) => (
            <tr key={g.groupId} className="border-t border-background-100">
              <td className="px-3 py-2 font-medium text-foreground-900">{g.groupName}<span className="tnum ml-1 text-foreground-500 font-normal">({g.groupCode})</span></td>
              <td className="tnum px-3 py-2 font-medium">{g.ticketNumber}</td>
              <td className="px-3 py-2 text-right"><Money value={g.totalCharged} /></td>
              <td className="px-3 py-2 text-right text-success-600 font-medium"><Money value={g.totalDividend} /></td>
              <td className="px-3 py-2 text-right font-semibold"><Money value={g.totalPaid} /></td>
              <td className="px-3 py-2 text-right font-semibold text-danger-600"><Money value={g.totalOutstanding} /></td>
              <td className="px-3 py-2">{g.wonCycle != null ? `Cycle ${g.wonCycle}` : "—"}</td>
              <td className="tnum px-3 py-2 text-right">{g.cyclesRemaining}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6">
        <h3 className="mb-2.5 border-b border-background-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-foreground-700">Combined position</h3>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-background-200 bg-background-200 sm:grid-cols-3">
          <div className="bg-background-50 p-3.5">
            <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">Total outstanding</p>
            <p className={`tnum mt-1 text-base font-bold ${statement.totalOutstanding > 0 ? "text-danger-600" : "text-foreground-900"}`}>{formatINR(statement.totalOutstanding)}</p>
          </div>
          <div className="bg-background-50 p-3.5">
            <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">Total dividend earned</p>
            <p className="tnum mt-1 text-base font-bold text-foreground-900">{formatINR(statement.totalDividend)}</p>
          </div>
          <div className="bg-background-50 p-3.5">
            <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">Payouts taken</p>
            <p className="tnum mt-1 text-base font-bold text-foreground-900">{statement.payoutCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
}